/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {logInfo, logWarning} from "App/debug";
import Messenger from "App/messenger";
import Texture from "App/render/webgl/renderables/texture";
import Canvas from "App/canvas";
import IScene from "js_interface/src/scene_interface";

export class features {
    public FP_TEXTURES: string = "OES_texture_float";
    public FPL_TEXTURES: string = "OES_texture_float_linear";
    public HFP_TEXTURES: string = "OES_texture_half_float";
    public HFPL_TEXTURES: string = "OES_texture_half_float_linear";
    public STD_DERIVATIVES: string = "OES_standard_derivatives";
    public DRAW_BUFFERS: string = "WEBGL_draw_buffers";
    public INDEX_UINT: string = "OES_element_index_uint";
    public DEPTH_TEXTURE: string = "WEBGL_depth_texture";
    public COMPRESSED_TEXTURE_S3TC: string = "WEBGL_compressed_texture_s3tc";
    public COMPRESSED_TEXTURE_PVRTC: string = "WEBGL_compressed_texture_pvrtc";
    public COMPRESSED_TEXTURE_ETC1: string = "WEBGL_compressed_texture_etc1";
    public VERTEX_ARRAY_OBJECT: string = "OES_vertex_array_object";
    public INSTANCING: string = "ANGLE_instanced_arrays";
    public FRAG_DEPTH: string = "EXT_frag_depth";
    public TEXTURE_FILTER_ANISOTROPIC: string = "EXT_texture_filter_anisotropic"
}

export class webgl20Features  {
    public FP_TEXTURES: string = "core";
    public FPL_TEXTURES: string = "OES_texture_float_linear";
    public HFP_TEXTURES: string = "core";
    public HFPL_TEXTURES: string = "OES_texture_half_float_linear";
    public STD_DERIVATIVES: string = "core";
    public DRAW_BUFFERS: string = "core";
    public INDEX_UINT: string = "core";
    public DEPTH_TEXTURE: string = "core";
    public COMPRESSED_TEXTURE_S3TC: string = "WEBGL_compressed_texture_s3tc";
    public COMPRESSED_TEXTURE_PVRTC: string = "WEBGL_compressed_texture_pvrtc";
    public COMPRESSED_TEXTURE_ETC1: string = "WEBGL_compressed_texture_etc1";
    public VERTEX_ARRAY_OBJECT: string = "core";
    public INSTANCING: string = "core";
    public FRAG_DEPTH: string = "core";
    public TEXTURE_FILTER_ANISOTROPIC: string = "EXT_texture_filter_anisotropic";
    public COLOR_BUFFER_FLOAT: string = "EXT_color_buffer_float"
}

export default
class WebGLContext {

    public static isWebGL2: boolean = false;
    public static useInstancing: boolean = false;
    public static useVAO: boolean = false;
    public static hasAnisFiltering: boolean = false;
    public static hasFragDepth: boolean = false;

    public static MAX_USER_ANISOTROPY_FILTERING_VALUE: number = 4;

    // init of capabilities of the context
    public static caps: any = {
        PLATFORM: navigator.platform,
        AGENT: navigator.userAgent
    };

    // Extensions
    public static ext: any = {};

    public static features: any = new features();

    private _preserveDrawingBuffer: boolean;
    private ctx3d: WebGLRenderingContext = null;
    private _canvas: Canvas;
    private _sceneAPI: IScene;
    private _isValid: boolean;

    constructor(aCanvas: Canvas, aPreserverDrawingBufferBecausOfWebVR: boolean) {
        this._preserveDrawingBuffer = aPreserverDrawingBufferBecausOfWebVR;

        this._canvas = aCanvas;
        this._sceneAPI = aCanvas.getSceneAPI();

        this._isValid = this.setupContext();
    }

    public getWebGLRenderingContext(): WebGLRenderingContext {
        return this.ctx3d;
    }

    public setupContext(): boolean {
        let validContextNames = ['webgl2', 'webgl', 'experimental-webgl', 'moz-webgl', 'webkit-3d'];

        // Context creation params
        // preserveDrawingBuffer notes:
        // have to be false for production otherwise performance problems can occur; consequently for e.g quad iew we need to redraw all views everytime, but the z-buffer will be cleared everyframe
        // for WebVR is muset be set to true otherwise the output will be not mirrord to the VR display
        let ctxAttribs = {
            alpha: true,
            depth: true,
            stencil: true,
            antialias: true,
            premultipliedAlpha: false,
            preserveDrawingBuffer: this._preserveDrawingBuffer
        };

        for (let i = 0; i < validContextNames.length; i++) {
            try {
                // Get context from canvas
                let ctx: any = this._canvas.getHTMLCanvas().getContext(validContextNames[i], ctxAttribs);
                if (!ctx) {
                    continue;
                }

                // Save it's name for debug purposes
                ctx.name = validContextNames[i];
                this.ctx3d = ctx;

                // Get extensions if wasn't loaded before
                this.initExtensions(ctx);

                return true;
            } catch (e) {
                logWarning(e);
            }
        }

        return false;
    }

    /**
     * @param {WebGLRenderingContext} ctx
     */
    public initExtensions(ctx: WebGLRenderingContext) {
        //Save CAPS
        WebGLContext.caps.VENDOR = ctx.getParameter(ctx.VENDOR);
        WebGLContext.caps.VERSION = ctx.getParameter(ctx.VERSION);
        WebGLContext.caps.RENDERER = ctx.getParameter(ctx.RENDERER);
        WebGLContext.caps.SHADING_LANGUAGE_VERSION = ctx.getParameter(ctx.SHADING_LANGUAGE_VERSION);
        WebGLContext.caps.RED_BITS = ctx.getParameter(ctx.RED_BITS);
        WebGLContext.caps.GREEN_BITS = ctx.getParameter(ctx.GREEN_BITS);
        WebGLContext.caps.BLUE_BITS = ctx.getParameter(ctx.BLUE_BITS);
        WebGLContext.caps.ALPHA_BITS = ctx.getParameter(ctx.ALPHA_BITS);
        WebGLContext.caps.DEPTH_BITS = ctx.getParameter(ctx.DEPTH_BITS);
        WebGLContext.caps.MAX_VERTEX_ATTRIBS = ctx.getParameter(ctx.MAX_VERTEX_ATTRIBS);
        WebGLContext.caps.MAX_VERTEX_TEXTURE_IMAGE_UNITS = ctx.getParameter(ctx.MAX_VERTEX_TEXTURE_IMAGE_UNITS);
        WebGLContext.caps.MAX_VARYING_VECTORS = ctx.getParameter(ctx.MAX_VARYING_VECTORS);
        WebGLContext.caps.MAX_VERTEX_UNIFORM_VECTORS = ctx.getParameter(ctx.MAX_VERTEX_UNIFORM_VECTORS);
        WebGLContext.caps.MAX_COMBINED_TEXTURE_IMAGE_UNITS = ctx.getParameter(ctx.MAX_COMBINED_TEXTURE_IMAGE_UNITS);
        WebGLContext.caps.MAX_TEXTURE_SIZE = ctx.getParameter(ctx.MAX_TEXTURE_SIZE);
        WebGLContext.caps.MAX_TEXTURE_IMAGE_UNITS = ctx.getParameter(ctx.MAX_TEXTURE_IMAGE_UNITS);
        WebGLContext.caps.MAX_CUBE_MAP_TEXTURE_SIZE = ctx.getParameter(ctx.MAX_CUBE_MAP_TEXTURE_SIZE);
        WebGLContext.caps.COMPRESSED_TEXTURE_FORMATS = ctx.getParameter(ctx.COMPRESSED_TEXTURE_FORMATS);
        WebGLContext.caps.MAX_RENDERBUFFER_SIZE = ctx.getParameter(ctx.MAX_RENDERBUFFER_SIZE);
        WebGLContext.caps.MAX_VIEWPORT_DIMS = ctx.getParameter(ctx.MAX_VIEWPORT_DIMS);
        WebGLContext.caps.ALIASED_LINE_WIDTH_RANGE = ctx.getParameter(ctx.ALIASED_LINE_WIDTH_RANGE);
        WebGLContext.caps.ALIASED_POINT_SIZE_RANGE = ctx.getParameter(ctx.ALIASED_POINT_SIZE_RANGE);
        WebGLContext.caps.SAMPLES = ctx.getParameter(ctx.SAMPLES);
        WebGLContext.caps.EXTENSIONS = ctx.getSupportedExtensions();

        let highp = ctx.getShaderPrecisionFormat(ctx.FRAGMENT_SHADER, ctx.HIGH_FLOAT);
        WebGLContext.caps.PRECISION_HIGHP = highp.precision !== 0;

        // Load extensions
        //@ts-ignore
        if (ctx.name.indexOf("webgl2") !== -1) { // webgl 20
            WebGLContext.features = new webgl20Features();
            WebGLContext.isWebGL2 = true;
        }

        for (let key in WebGLContext.features) {
            WebGLContext.loadExtension(ctx, WebGLContext.features[key]);
        }

        // Some "class" variables
        WebGLContext.useVAO = WebGLContext.getFeatureAvailable("VERTEX_ARRAY_OBJECT");
        WebGLContext.useInstancing = WebGLContext.getFeatureAvailable("INSTANCING");
        WebGLContext.hasFragDepth = WebGLContext.getFeatureAvailable("FRAG_DEPTH");

        // Set anisotropy filtering max value
        let anisFiltering = WebGLContext.getFeatureAsExtension("TEXTURE_FILTER_ANISOTROPIC");
        if (anisFiltering) {
            WebGLContext.hasAnisFiltering = true;
            let max = ctx.getParameter(anisFiltering.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
            if (max > WebGLContext.MAX_USER_ANISOTROPY_FILTERING_VALUE) {
                max = WebGLContext.MAX_USER_ANISOTROPY_FILTERING_VALUE;
            }
            WebGLContext.caps.MAX_TEXTURE_MAX_ANISOTROPY_EXT_VALUE = max;
        }

        // For float textures
        if (WebGLContext.getFeatureAvailable("FP_TEXTURES")) {
            WebGLContext.caps.FLOAT32 = ctx.FLOAT;
        } else {
            logInfo("Texture.FLOAT32 is not supported");
        }

        // This is used for Half_float textures
        if (WebGLContext.getFeatureAvailable("HFP_TEXTURES")) {
            let ext = WebGLContext.getFeatureAsExtension("HFP_TEXTURES");
            if (ext) {
                WebGLContext.caps.FLOAT16 = ext.HALF_FLOAT_OES;
            } else {
                // @ts-ignore
                WebGLContext.caps.FLOAT16 = ctx.HALF_FLOAT;
            }
        } else {
            logInfo("Texture.FLOAT16 is not supported");
        }

        // Debug message
        let extString = WebGLContext.caps.EXTENSIONS.toString().replace(/,/g, ", ");
        // @ts-ignore
        logInfo(ctx.name + " context found\nVendor: " + WebGLContext.caps.VENDOR +
            ", Renderer: " + WebGLContext.caps.RENDERER + ", " + "Version: " + WebGLContext.caps.VERSION + ", " +
            "ShadingLangV.: " + WebGLContext.caps.SHADING_LANGUAGE_VERSION +
            ", MSAA samples: " + WebGLContext.caps.SAMPLES + "\nExtensions: " + extString);

        // Fire events for un/supported effects
        // At least any float texture
        if (!WebGLContext.caps.FLOAT32 && !WebGLContext.caps.FLOAT16) {
            Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_shadowMapsUnsupported", {});
            Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_SSAOUnsupported", {});
        }
        // Without instancing
        if (!WebGLContext.getFeatureAvailable("INSTANCING")) {
            Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_groundPlaneGrassUnsupported", {});
            Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_shadowVolumesUnsupported", {});
        }
    }

    /**
     * @param {WebGLRenderingContext} context
     * @param {string} extension
     */
    public static loadExtension(context: WebGLRenderingContext, extension: string) {
        if (extension === "core") {
            WebGLContext.ext[extension] = null;
            return;
        }

        let ext =
            context.getExtension(extension) ||
            context.getExtension('MOZ_' + extension) ||
            context.getExtension('WEBKIT_' + extension);

        if (ext) {
            logInfo("Extension found: " + extension);
            WebGLContext.ext[extension] = ext;
        } else {
            logInfo("Extension NOT found: " + extension);
        }
    }

    /**
     * @param {string} extension
     * @return {object | undefined}
     */
    public static getFeatureAsExtension(feature: string) {
        if (WebGLContext.features[feature] === "core") {
            return null;
        }

        return WebGLContext.ext[WebGLContext.features[feature]];
    }

    public static getIsFeatureCore(feature: string): boolean {
        return WebGLContext.features[feature] === "core";
    }

    public static getFeatureAvailable(feature: string): boolean {
        return WebGLContext.features[feature] === "core" || WebGLContext.getFeatureAsExtension(feature) !== undefined;
    }

    public getCanvas(): Canvas {
        return this._canvas;
    }

    public isValid(): boolean {
        return this._isValid;
    }

    public release() {
    }

    public static checkRequiredOpenGLFeatures(aErrorMessages: string[]) {
        if (WebGLContext.caps.MAX_VERTEX_ATTRIBS < 5) {
            aErrorMessages.push("Only " + WebGLContext.caps.MAX_VERTEX_ATTRIBS + " vertex attributes available, but at least 5 is required.");
        }

        if (WebGLContext.caps.MAX_VARYING_VECTORS < 5) {
            aErrorMessages.push("Only " + WebGLContext.caps.MAX_VARYING_VECTORS + " varying vectors available, but at least 5 required.");
        }

        if (WebGLContext.caps.MAX_VERTEX_UNIFORM_VECTORS < 10) {
            aErrorMessages.push("Only " + WebGLContext.caps.MAX_VERTEX_UNIFORM_VECTORS +
                " vertex uniform vectors available, but at least 10 required.");
        }

        if (WebGLContext.caps.MAX_TEXTURE_SIZE < 2048) {
            aErrorMessages.push("Only " + WebGLContext.caps.MAX_TEXTURE_SIZE + " texture resolution available, but at least 2048 required.");
        }

        return (aErrorMessages.length === 0);
    }

    public static checkOptionalOpenGLFeatures(aWarningMessages: string[]) {
        if (!WebGLContext.getFeatureAvailable("INDEX_UINT")) {
            aWarningMessages.push("OES_element_index_uint is not supported, only the first 65.535 vertices from each element will be used for rendering.");
        }

        if (!WebGLContext.hasAnisFiltering) {
            aWarningMessages.push("XXX_texture_filter_anisotropic is not supported, the textures in the model will have lower qaulity.");
        }

        if (!WebGLContext.caps.PRECISION_HIGHP) {
            aWarningMessages.push("The higp precision qualifier is not available, but is required for correct transparency and scene manipulation.");
        }

        if (!WebGLContext.getFeatureAvailable("DEPTH_TEXTURE")) {
            //a_warning_messages.push("WEBGL_depth_texture is not supported");
        }

        if (!WebGLContext.caps.COMPRESSED_TEXTURE) {
            //a_warning_messages.push("WEBGL_compressed_texture_s3tc is not supported.");
        }

        return (aWarningMessages.length === 0);
    }
}
